<script setup lang="ts">
const localePath = useLocalePath();
const businessStore = useBusinessStore();
const {
  isMenuOpen,
  business,
  selectedLocation,
  isSearchOpen,
  cartCount,
  canRefreshServices,
} = storeToRefs(businessStore);

const { isLoggedIn } = useAuth();
const { t } = useI18n();
const { showSectionSelector, selectedGender, openSelector } = useServiceGender();

const currentSectionLabel = computed(() =>
  selectedGender.value
    ? t(`service_gender.${selectedGender.value}`)
    : t("service_gender.both"),
);

const client = useClient();
const cartCookie =
  useCookie<
    { location_id?: number; product_location_id: number; quantity: number }[]
  >("cart");

const route = useRoute();

const isModuleActive = (module: string) => {
  if (module === "about_us") {
    // Exact match for the location root
    return route.path === localePath(`/${selectedLocation.value?.identifier}/`);
  }
  return route.path.includes(`/${module}`);
};

const handleMenuClick = async (destination: string) => {
  if (destination === "services") {
    canRefreshServices.value = true;
    nextTick(() => {
      businessStore.clearBooking();
    });
  }
  isMenuOpen.value = false;
  isSearchOpen.value = false;
};

const handleSectionClick = () => {
  isMenuOpen.value = false;
  isSearchOpen.value = false;
  openSelector();
};

const cartItemCount = computed(() => {
  if (isLoggedIn.value) {
    return cartCount.value;
  }

  return (
    cartCookie.value
      ?.filter((item) => item.location_id === selectedLocation.value?.id)
      .reduce((sum, item) => sum + item.quantity, 0) || 0
  );
});
</script>

<template>
  <Transition name="menu-slide">
    <UCard
      v-if="isMenuOpen"
      class="flex flex-col flex-1 h-full fixed lg:absolute inset-y-0 start-0 w-[85%] md:w-1/2 lg:w-[calc(100%-200px)] border-e border-gray-100 bg-white overflow-hidden z-30"
      :ui="{
        body: { base: 'h-full min-h-0 overflow-y-auto', padding: 'py-5' },
        footer: { padding: 'px-10 py-5' },
        ring: '',
        divide: 'divide-y divide-gray-100 dark:divide-gray-800',
        rounded: 'rounded-e-2xl md:rounded-e-3xl lg:rounded-e-3xl',
      }"
    >
      <template #header>
        <div class="shrink-0 flex items-center gap-x-1">
          <UButton
            icon="i-approved-arrow-left"
            size="xl"
            class="rounded-xl"
            color="gray"
            variant="ghost"
            :ui="{ square: { xl: 'p-2' } }"
            @click="isMenuOpen = false"
          />
          <div class="flex items-center gap-2.5">
            <img v-if="selectedLocation?.logo" :src="selectedLocation?.logo" :alt="business?.name" class="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover" >
            <div class="flex flex-col">
              <h2 class="text-gray-900 text-xl font-bold capitalize">
                {{ selectedLocation?.name }}
              </h2>
              <p class="text-gray-500 text-sm ltr:first-letter:uppercase">
                {{ selectedLocation?.targeted_market }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <nav>
        <ul class="*:text-gray-900">
          <li>
            <NuxtLink
              :to="localePath(`/${selectedLocation?.identifier}/`)"
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('about_us') }"
              @click="handleMenuClick"
            >
              <UIcon name="i-approved-about" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.about_us")
              }}</span>
            </NuxtLink>
          </li>

          <li>
            <NuxtLink
              :to="
                selectedLocation
                  ? localePath(
                      `/${selectedLocation.identifier}/booking/services`
                    )
                  : ''
              "
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('booking') }"
              @click="handleMenuClick('services')"
            >
              <UIcon name="i-approved-services" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.services")
              }}</span>
            </NuxtLink>
          </li>
          <li v-if="showSectionSelector">
            <button
              type="button"
              class="w-full px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              @click="handleSectionClick"
            >
              <UIcon name="i-approved-group" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.section")
              }}</span>
              <span
                class="ms-auto text-sm font-medium text-gray-500 first-letter:uppercase"
                >{{ currentSectionLabel }}</span
              >
            </button>
          </li>
          <li
            v-if="
              business?.payment_methods?.some(
                (method) => method.can_buy_membership
              )
            "
          >
            <NuxtLink
              :to="
                selectedLocation
                  ? localePath(`/${selectedLocation.identifier}/memberships`)
                  : ''
              "
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('memberships') }"
              @click="handleMenuClick"
            >
              <UIcon name="i-approved-memberships" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.memberships")
              }}</span>
            </NuxtLink>
          </li>
          <li
            v-if="
              business?.payment_methods?.some(
                (method) => method?.can_buy_product
              )
            "
          >
            <NuxtLink
              :to="
                selectedLocation
                  ? localePath(`/${selectedLocation.identifier}/products`)
                  : ''
              "
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('products') }"
              @click="handleMenuClick"
            >
              <UIcon name="i-approved-products" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.products")
              }}</span>
            </NuxtLink>
          </li>
          <li
            v-if="
              business?.payment_methods?.some(
                (method) => method?.can_buy_gift_card
              )
            "
          >
            <NuxtLink
              :to="
                selectedLocation
                  ? localePath(`/${selectedLocation.identifier}/gift-cards`)
                  : ''
              "
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('gift-cards') }"
              @click="handleMenuClick"
            >
              <UIcon name="i-approved-gift-card" class="size-7" />
              <span class="text-xl font-bold first-letter:uppercase">{{
                $t("menu.gift_cards")
              }}</span>
            </NuxtLink>
          </li>
          <li
            v-if="
              business?.payment_methods?.some(
                (method) => method.can_buy_product
              )
            "
          >
            <NuxtLink
              :to="localePath(`/${selectedLocation?.identifier}/cart`)"
              class="px-10 py-5 flex items-center gap-x-1.5 hover:transition-[color] hover:text-primary"
              :class="{ 'text-primary': isModuleActive('cart') }"
              @click="handleMenuClick"
            >
              <div class="relative">
                <UIcon name="i-approved-shopping-cart" class="size-7" />
                <span
                  v-if="cartItemCount"
                  class="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-furious-purple rounded-full"
                  >{{ cartItemCount }}</span
                >
              </div>
              <span
                class="text-xl font-bold first-letter:uppercase"
                :class="{ 'ms-1': cartItemCount }"
                >{{ $t("menu.cart") }}</span
              >
            </NuxtLink>
          </li>
          <template v-if="isLoggedIn">
            <li v-if="!client?.profile_completed">
              <NuxtLink
                :to="localePath(`/complete`)"
                class="text-xl font-bold px-10 py-5 block capitalize"
                :class="{
                  'text-primary': route.path === localePath('/complete'),
                }"
                @click="handleMenuClick"
              >
                {{ $t("menu.complete_profile") }}
              </NuxtLink>
            </li>
          </template>
        </ul>
      </nav>

      <template #footer>
        <div class="shrink-0 flex flex-col gap-y-4">
          <div class="flex items-center justify-between lg:hidden">
            <span
              class="text-sm text-gray-400 font-medium uppercase tracking-wider"
              >{{ $t("label.language") }}</span
            >
            <CommonLanguagesMenu />
          </div>

          <div class="flex flex-col">
            <span class="text-sm text-gray-500 font-medium"
              >{{ $t("powered_by") }}
              <NuxtLink :to="localePath('/')" class="text-primary underline"
                >bookforme.com</NuxtLink
              ></span
            >
            <span class="text-sm text-gray-500 font-medium">{{
              $t("all_rights_reserved", {
                year: `© ${new Date().getFullYear()}`,
              })
            }}</span>
          </div>
        </div>
      </template>
    </UCard>
  </Transition>
</template>
